import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useDispatch } from "react-redux";
import DashboardLayout from "../../Layout/DashboardLayout";
import { companyID, ExportExcel } from "../../Common/UIComponents";
import {
  fetchShiftChangeoverReportData,
  fetchShiftChangeoverFacilityList,
} from "../../API_Service/Reports_Slice";

// ─── Constants ───────────────────────────────────────────────────────────────
const PAGE_SIZE_OPTIONS = [10, 25, 50];

// ─── Pagination ───────────────────────────────────────────────────────────────
function Pagination({ currentPage, totalPages, onPageChange }) {
  const { pages, showEllipsis, lastPage } = useMemo(() => {
    if (totalPages <= 5) {
      return {
        pages: Array.from({ length: totalPages }, (_, i) => i + 1),
        showEllipsis: false,
      };
    }
    return { pages: [1, 2, 3], showEllipsis: true, lastPage: totalPages };
  }, [totalPages]);

  const btnCls = (active) =>
    `w-7 h-7 flex items-center justify-center text-xs font-medium rounded-md ${
      active
        ? "bg-[#08757c] text-white font-bold"
        : "hover:bg-[#e5e9e9] text-[#181c1d]"
    }`;

  return (
    <div className="flex items-center gap-1">
      <button
        onClick={() => onPageChange(1)}
        disabled={currentPage === 1}
        className="p-1 hover:bg-[#e5e9e9] rounded transition-colors disabled:opacity-40"
      >
        <span className="material-symbols-outlined text-sm">first_page</span>
      </button>
      <button
        onClick={() => onPageChange(currentPage - 1)}
        disabled={currentPage === 1}
        className="p-1 hover:bg-[#e5e9e9] rounded transition-colors disabled:opacity-40"
      >
        <span className="material-symbols-outlined text-sm">chevron_left</span>
      </button>
      <div className="flex items-center mx-2 gap-1">
        {pages.map((p) => (
          <button
            key={p}
            onClick={() => onPageChange(p)}
            className={btnCls(currentPage === p)}
          >
            {p}
          </button>
        ))}
        {showEllipsis && (
          <>
            <span className="text-xs px-1 text-[#6e797a]">...</span>
            <button
              onClick={() => onPageChange(lastPage)}
              className={btnCls(currentPage === lastPage)}
            >
              {lastPage}
            </button>
          </>
        )}
      </div>
      <button
        onClick={() => onPageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
        className="p-1 hover:bg-[#e5e9e9] rounded transition-colors disabled:opacity-40"
      >
        <span className="material-symbols-outlined text-sm">chevron_right</span>
      </button>
      <button
        onClick={() => onPageChange(totalPages)}
        disabled={currentPage === totalPages}
        className="p-1 hover:bg-[#e5e9e9] rounded transition-colors disabled:opacity-40"
      >
        <span className="material-symbols-outlined text-sm">last_page</span>
      </button>
    </div>
  );
}

// ─── Facility Dropdown ────────────────────────────────────────────────────────
function FacilityDropdown({ facilities, value, onChange }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const containerRef = useRef(null);

  const getId = (f) => f.FacilityID;
  const getName = (f) => f.FacilityName;

  const selected = facilities.find((f) => getId(f) === value);
  const label = selected ? getName(selected) : "Select Facility";

  const filtered = useMemo(() => {
    if (!query.trim()) return facilities;
    const q = query.toLowerCase();
    return facilities.filter((f) => getName(f)?.toLowerCase().includes(q));
  }, [facilities, query]);

  useEffect(() => {
    function handleClickOutside(e) {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
        setQuery("");
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  function select(id) {
    onChange(id);
    setOpen(false);
    setQuery("");
  }

  return (
    <div ref={containerRef} className="relative w-full">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between px-4 py-2.5 bg-[#f1f4f4] rounded-lg text-sm text-[#181c1d] focus:outline-none focus:ring-2 focus:ring-[#08757c] transition-all"
      >
        <span className="truncate">{label}</span>
        <span
          className={`material-symbols-outlined text-[18px] text-[#6e797a] transition-transform duration-200 ${
            open ? "rotate-180" : ""
          }`}
        >
          expand_more
        </span>
      </button>

      {open && (
        <div className="absolute z-50 mt-1 w-full bg-white rounded-xl shadow-lg border border-[#e0e3e3] overflow-hidden">
          <div className="p-2 border-b border-[#f1f4f4]">
            <div className="relative">
              <span className="material-symbols-outlined absolute left-2.5 top-1/2 -translate-y-1/2 text-[16px] text-[#6e797a]">
                search
              </span>
              <input
                autoFocus
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search facility..."
                className="w-full pl-8 pr-3 py-1.5 bg-[#f1f4f4] rounded-lg text-xs focus:outline-none focus:ring-1 focus:ring-[#08757c]"
              />
            </div>
          </div>

          <ul className="max-h-52 overflow-y-auto py-1">
            {filtered.length > 0 ? (
              filtered.map((f) => (
                <li
                  key={getId(f)}
                  onClick={() => select(getId(f))}
                  className={`px-4 py-2 text-sm cursor-pointer transition-colors ${
                    value === getId(f)
                      ? "bg-[#08757c]/10 text-[#08757c] font-semibold"
                      : "hover:bg-[#f1f4f4] text-[#181c1d]"
                  }`}
                >
                  {getName(f)}
                </li>
              ))
            ) : (
              <li className="px-4 py-3 text-xs text-[#6e797a] text-center">
                No facilities found
              </li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
}

// ─── Dynamic Table ────────────────────────────────────────────────────────────

const ALLOWED_COLUMNS = [
  "Start_Time",
  "End_Time",
  "ProjectCode",
  "Project_Name",
  "Equipment_Test_Type_nm",
  "Activity_Name",
  "Accessories and Harness location",
  "Alternate Project",
  "Change from previous day",
  "Remarks",
  "Setup status",
  "Test status",
  "Testing Status",
];

function ShiftChangeOverTable({ rows }) {
  if (!rows.length) return null;
  const availableKeys = new Set(Object.keys(rows[0]));
  const columns = ALLOWED_COLUMNS.filter((col) => availableKeys.has(col));

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-left border-collapse">
        <thead>
          <tr className="bg-[#f1f4f4] border-b border-[#e0e3e3]">
            {columns.map((col) => (
              <th
                key={col}
                className="px-4 py-3 text-xs font-bold text-[#3e494a] uppercase tracking-wider whitespace-nowrap"
              >
                {col.replace(/_/g, " ")}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-[#e0e3e3]">
          {rows.map((row, idx) => (
            <tr key={idx} className="hover:bg-[#f1f4f4] transition-colors">
              {columns.map((col) => (
                <td
                  key={col}
                  className="px-4 py-3 text-xs text-[#181c1d] whitespace-nowrap"
                >
                  {row[col] ?? "-"}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function ShiftChangeOverReport() {
  const dispatch = useDispatch();

  const [facID, setFacID] = useState(0);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [pageSize, setPageSize] = useState(25);
  const [currentPage, setCurrentPage] = useState(1);

  const [facilities, setFacilities] = useState([]);
  const [records, setRecords] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [loading, setLoading] = useState(false);
  const [facError, setFacError] = useState("");

  const totalPages = Math.max(1, Math.ceil(totalRecords / pageSize));

  // ── Fetch report data ──────────────────────────────────────────────────────
  const fetchData = useCallback(
    async (page, size, selectedFacID, from, to) => {
      setLoading(true);
      try {
        const start = (page - 1) * size;
        const result = await dispatch(
          fetchShiftChangeoverReportData({
            facID: selectedFacID,
            fromDate: from,
            toDate: to,
            start,
            length: size,
            sortColumn: 0,
            sortColumnDir: "desc",
            search: "",
            draw: String(page),
            companyId: companyID,
          }),
        ).unwrap();

        const responseData = result?.data;
        setRecords(responseData?.data ?? []);
        setTotalRecords(responseData?.recordsTotal ?? 0);
      } catch {
        setRecords([]);
        setTotalRecords(0);
      } finally {
        setLoading(false);
      }
    },
    [dispatch],
  );

  // ── Load facilities on mount ───────────────────────────────────────────────
  useEffect(() => {
    dispatch(fetchShiftChangeoverFacilityList({ companyId: companyID }))
      .unwrap()
      .then((res) => setFacilities(res?.data ?? []))
      .catch(() => {});
  }, []);

  // ── Auto-fetch when facility/dates change ──────────────────────────────────
  useEffect(() => {
    if (facID === 0) return;
    setCurrentPage(1);
    fetchData(1, pageSize, facID, fromDate, toDate);
  }, [facID, fromDate, toDate]);

  function handlePageChange(page) {
    setCurrentPage(page);
    fetchData(page, pageSize, facID, fromDate, toDate);
  }

  function handlePageSizeChange(size) {
    setPageSize(size);
    setCurrentPage(1);
    fetchData(1, size, facID, fromDate, toDate);
  }

  function handleClear() {
    setFacID(0);
    setFromDate("");
    setToDate("");
    setFacError("");
    setRecords([]);
    setTotalRecords(0);
    setCurrentPage(1);
  }

  // ── Export ─────────────────────────────────────────────────────────────────
  const exportHeaders = records.length
    ? ALLOWED_COLUMNS.filter((col) => col in records[0])
    : [];
  const exportData = records.map((r) => exportHeaders.map((h) => r[h]));

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <DashboardLayout>
      <div className="p-8 space-y-8">
        {/* Page Title */}
        <h1 className="text-xl font-bold tracking-tight text-[#08757c]">
          Shift Change Over Dump Report
        </h1>

        {/* ── Filters ── */}
        <section className="bg-white rounded-xl p-6 shadow-sm">
          <div className="flex flex-wrap items-end gap-6">
            {/* Facility */}
            <div className="flex-1 min-w-[280px]">
              <label className="block text-xs font-semibold text-[#3e494a] mb-2">
                Facility Selection <span className="text-red-500">*</span>
              </label>
              <FacilityDropdown
                facilities={facilities}
                value={facID}
                onChange={(id) => {
                  setFacID(id);
                  setFacError("");
                }}
              />
              {facError && (
                <p className="mt-1 text-xs text-red-500 flex items-center gap-1">
                  <span className="material-symbols-outlined text-[14px]">
                    error
                  </span>
                  {facError}
                </p>
              )}
            </div>

            {/* From Date */}
            <div className="w-48">
              <label className="block text-xs font-semibold text-[#3e494a] mb-2">
                From Date
              </label>
              <input
                type="date"
                value={fromDate}
                onChange={(e) => {
                  setFromDate(e.target.value);
                  setToDate("");
                }}
                onKeyDown={(e) => e.preventDefault()}
                onClick={(e) => e.currentTarget.showPicker?.()}
                className="w-full px-4 py-2.5 bg-[#f1f4f4] border-none rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#08757c] transition-all cursor-pointer"
              />
            </div>

            {/* To Date */}
            <div className="w-48">
              <label className="block text-xs font-semibold text-[#3e494a] mb-2">
                To Date
              </label>
              <input
                type="date"
                value={toDate}
                min={fromDate || undefined}
                onChange={(e) => setToDate(e.target.value)}
                onKeyDown={(e) => e.preventDefault()}
                onClick={(e) => e.currentTarget.showPicker?.()}
                className="w-full px-4 py-2.5 bg-[#f1f4f4] border-none rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#08757c] transition-all cursor-pointer"
              />
            </div>

            {/* Action Buttons */}
            <div className="flex items-center gap-3">
              <button
                onClick={handleClear}
                className="flex items-center gap-2 px-5 py-2.5 bg-[#f1f4f4] hover:bg-[#e0e3e3] text-[#3e494a] text-sm font-semibold rounded-lg transition-colors"
              >
                <span className="material-symbols-outlined text-[18px]">
                  refresh
                </span>
                Clear
              </button>

              {/* Export */}
              <div
                title={!facID ? "Select the facility first" : ""}
                onClick={(e) => {
                  if (!facID) {
                    e.stopPropagation();
                    setFacError("Facility selection is required.");
                  }
                }}
              >
                <ExportExcel
                  headers={exportHeaders}
                  data={exportData}
                  filename="shift_changeover_report"
                  buttonLabel="Export Excel"
                  disabled={records.length === 0 || !facID}
                />
              </div>
            </div>
          </div>
        </section>

        {/* ── Report Table ── */}
        <section className="bg-white rounded-xl overflow-hidden shadow-sm">
          {loading ? (
            <div className="flex items-center justify-center gap-2 text-[#08757c] py-12">
              <span className="material-symbols-outlined animate-spin">
                progress_activity
              </span>
              <span className="text-sm">Loading report data...</span>
            </div>
          ) : records.length > 0 ? (
            <ShiftChangeOverTable rows={records} />
          ) : (
            <div className="px-6 py-12 text-center text-sm text-[#3e494a]">
              No records found. Select a facility and generate the report.
            </div>
          )}

          {/* ── Pagination Footer ── */}
          <footer className="flex items-center justify-between px-6 py-4 bg-[#f1f4f4]">
            <div className="flex items-center gap-4">
              <span className="text-xs text-[#3e494a]">
                Total Records:{" "}
                <strong className="text-[#181c1d]">{totalRecords}</strong>
              </span>
              <div className="flex items-center gap-2">
                <label className="text-[10px] uppercase font-bold text-[#6e797a]">
                  Show
                </label>
                <select
                  value={pageSize}
                  onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                  className="bg-white border-none text-xs rounded-md py-1 px-2 focus:ring-0 cursor-pointer"
                >
                  {PAGE_SIZE_OPTIONS.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
            />
          </footer>
        </section>
      </div>
    </DashboardLayout>
  );
}
